import type Delta from "quill-delta";
import type {RichTextDocument, RichTextDocumentMark, RichTextDocumentNode} from "@o2d/rich-text-editor";

/** The subset of a Delta op's `attributes` this converter understands as
 *  BLOCK-level (apply to the whole line the op's `\n` ends), as opposed to
 *  INLINE (apply only to the text/embed the attributes sit directly on). */
const BLOCK_ATTR_KEYS = ["list", "indent", "align", "header", "blockquote", "code-block", "table"] as const;

/** Image attributes Quill stores on an image embed that the editor's `image` node also declares. */
const IMAGE_ATTR_KEYS = ["width", "height", "alt"] as const;

/** Quill's snow-theme default `size` format px-equivalents (no `.ql-size-*`
 *  rule exists in this app's own CSS — `ha-rte`/`ha-quill-viewer` have always
 *  relied on Quill's own built-in defaults for this one format). Reproduced
 *  here as `em` so the very same values keep applying under the TipTap
 *  `FontSize` mark. */
const SIZE_TO_FONT_SIZE: Record<string, string> = {
    small: "0.75em",
    large: "1.5em",
    huge: "2.5em",
};

interface QuillAttributes {
    [key: string]: unknown;
}

interface QuillOp {
    insert?: string | Record<string, unknown>;
    attributes?: QuillAttributes;
}

type InlineNode =
    | { kind: "text"; text: string; attrs: QuillAttributes }
    | { kind: "hardBreak" }
    | { kind: "image"; src: string; attrs: Record<string, unknown> };

/** An op that inserts nothing (`delete`, `retain`) and therefore has no place in a stored document. */
export interface SkippedDeltaOp {
    index: number;
    op: unknown;
}

/** A line whose Quill `list` value is none of the four formats Quill writes. */
export interface UnknownListFormat {
    line: number;
    value: unknown;
}

/** The converted document together with every op the conversion could not use. */
export interface DeltaConversionReport {
    document: RichTextDocument;
    skippedOps: SkippedDeltaOp[];
    /**
     * Lines rendered as a bullet list only because their `list` value was not recognised. Kept separate
     * from the document so the one-time migration can abort on it: a checklist with an unexpected value
     * would otherwise lose its checkboxes without a trace.
     */
    unknownListFormats: UnknownListFormat[];
}

/** The `list` values Quill writes; anything else still renders as a bullet list but is reported. */
const KNOWN_LIST_FORMATS = new Set<unknown>(["bullet", "ordered", "checked", "unchecked"]);

interface LineBlock {
    inline: InlineNode[];
    block: QuillAttributes;
}

/**
 * Converts a Quill `Delta` (this app's pre-2026-09 rich-text storage format,
 * still the live shape of every `Issue.description` and `LinkTile.text` row
 * that hasn't been re-saved since the RTE unification) into a
 * `RichTextDocument` — the tiptap/ProseMirror JSON shape `@o2d/rich-text-editor`
 * consumes.
 *
 * This is a LAZY, one-way migration step: called on read whenever a stored
 * value still looks like a Delta (see `RteValueHelper.isQuillDelta`), never
 * on write. There is no reverse conversion — once a field is saved again, it
 * is a `RichTextDocument` for good.
 *
 * ## What does and doesn't round-trip
 *
 * Every format this app's own `ha-rte` toolbar actually exposes converts:
 * bold/italic/strike, the soft line break (`lineBreak` embed → `hardBreak`
 * node), ordered/bullet/checklist lists WITH nesting (Quill's flat
 * `indent`-attribute model is rebuilt into TipTap's nested list-item tree),
 * super/subscript, the four `size` steps, text colour, background (→
 * `Highlight`, the closest TipTap equivalent to Quill's flat background
 * colour), alignment, and inserted images with their width, height and alt
 * text.
 *
 * The editor schema decides the shape, not Quill's flat line model:
 * - `image` is a BLOCK node, so a line is split at every image into
 *   paragraph / image / paragraph (empty halves are dropped, except the
 *   leading paragraph a list item must start with). An image in a heading or
 *   a code block, which only hold inline content or text, becomes its own
 *   block right after it. Inside a checklist item the image stays inline
 *   until the editor package allows blocks there.
 * - Quill tables (cell lines carrying `table: "<row id>"`) become
 *   table / tableRow / tableCell; short rows are padded with empty cells,
 *   because the editor rebuilds uneven tables on load.
 * - Consecutive code-block lines form ONE code block (joined with `\n`), and
 *   consecutive quote lines ONE blockquote.
 * - The inline `code` attribute becomes the `code` mark, which excludes every
 *   other mark on the same text.
 *
 * Also handled, though never reachable through this app's own toolbar
 * (commented out in `ha-rte.component.ts`, but not impossible in data pasted
 * from elsewhere or entered before a toolbar change): headings, blockquotes,
 * code blocks, and explicit links.
 *
 * The one deliberate drop: the zero-width-space (`​`) anchor
 * `ha-rte.component.ts` inserts after a trailing soft break purely to work
 * around a contenteditable rendering quirk (see its own doc comment) is
 * invisible content with no semantic meaning — it is filtered out here
 * rather than round-tripped into the new document.
 */
export class QuillDeltaToRichTextDocumentConverter {

    /**
     * Converts a Delta into a document. Ops that insert nothing are ignored; use
     * `convertWithReport` where they must be counted.
     */
    public static convert(delta: Delta | { ops?: unknown }): RichTextDocument {
        return this.convertWithReport(delta).document;
    }

    /**
     * Converts a Delta and lists every op that is not an insert (`delete`, `retain`). A stored Delta
     * should never contain one; the one-time migration reports them per row instead of dropping them
     * without a trace.
     */
    public static convertWithReport(delta: Delta | { ops?: unknown }): DeltaConversionReport {
        const ops = (Array.isArray((delta as { ops?: unknown })?.ops) ? (delta as { ops: QuillOp[] }).ops : []) as QuillOp[];
        const skippedOps: SkippedDeltaOp[] = [];
        const unknownListFormats: UnknownListFormat[] = [];
        const lines = this._toLines(ops, skippedOps);
        const content = this._linesToNodes(lines, unknownListFormats);

        return {
            document: {
                type: "doc",
                content: content.length > 0 ? content : [{type: "paragraph"}],
            },
            skippedOps,
            unknownListFormats,
        };
    }

    // ---- Delta ops → flat line list --------------------------------------

    private static _toLines(ops: QuillOp[], skippedOps: SkippedDeltaOp[]): LineBlock[] {
        const lines: LineBlock[] = [];
        let currentInline: InlineNode[] = [];
        let currentBlockAttrs: QuillAttributes = {};

        const closeLine = (attrsFromClosingOp: QuillAttributes | undefined) => {
            currentBlockAttrs = {...currentBlockAttrs, ...this._pickBlockAttrs(attrsFromClosingOp)};
            lines.push({inline: currentInline, block: currentBlockAttrs});
            currentInline = [];
            currentBlockAttrs = {};
        };

        ops.forEach((op, opIndex) => {
            if (op.insert === undefined) {
                skippedOps.push({index: opIndex, op});
                return;
            }
            if (typeof op.insert === "string") {
                const segments = op.insert.split("\n");
                segments.forEach((segment, index) => {
                    // Strip the ZWSP anchor `ha-rte.component.ts` inserts after a
                    // trailing soft break (see this class's own doc comment) — Quill
                    // composes adjacent same-attribute inserts into one op, so the
                    // anchor is not always its own separate segment; a plain global
                    // replace catches it wherever it landed.
                    const text = segment.replace(/​/g, "");
                    if (text.length > 0) {
                        currentInline.push({kind: "text", text, attrs: op.attributes ?? {}});
                    }
                    if (index < segments.length - 1) {
                        closeLine(op.attributes);
                    }
                });
            } else if (op.insert && typeof op.insert === "object") {
                if ("image" in op.insert && typeof op.insert.image === "string") {
                    currentInline.push({kind: "image", src: op.insert.image, attrs: this._pickImageAttrs(op.attributes)});
                } else if ("lineBreak" in op.insert) {
                    currentInline.push({kind: "hardBreak"});
                }
                // Any other/unknown embed (none exist in this app today) is
                // dropped rather than guessed at.
            }
        });

        // A well-formed Quill Delta always ends its last line with `\n`, so
        // this only fires for a malformed/hand-built Delta missing it.
        if (currentInline.length > 0) {
            lines.push({inline: currentInline, block: currentBlockAttrs});
        }

        return lines;
    }

    private static _pickImageAttrs(attrs: QuillAttributes | undefined): Record<string, unknown> {
        const picked: Record<string, unknown> = {};
        for (const key of IMAGE_ATTR_KEYS) {
            if (attrs?.[key] !== undefined && attrs[key] !== null) {
                picked[key] = attrs[key];
            }
        }
        return picked;
    }

    private static _pickBlockAttrs(attrs: QuillAttributes | undefined): QuillAttributes {
        if (!attrs) {
            return {};
        }
        const picked: QuillAttributes = {};
        for (const key of BLOCK_ATTR_KEYS) {
            if (attrs[key] !== undefined) {
                picked[key] = attrs[key];
            }
        }
        return picked;
    }

    // ---- Lines → RichTextDocument nodes, rebuilding list nesting ---------

    private static _linesToNodes(lines: LineBlock[], unknownListFormats: UnknownListFormat[]): RichTextDocumentNode[] {
        const root: RichTextDocumentNode[] = [];
        // Stack of currently-open list containers, innermost last. `level`
        // is the Quill `indent` value that container was opened at;
        // `node.content` is where new sibling list-items get pushed.
        const stack: { level: number; listType: string; node: RichTextDocumentNode }[] = [];

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];

            if (line.block.table !== undefined) {
                stack.length = 0;
                const end = this._runEnd(lines, index, candidate => candidate.block.table !== undefined);
                root.push(this._tableNode(lines.slice(index, end)));
                index = end - 1;
                continue;
            }

            const listFormat = line.block.list as string | undefined;

            if (!listFormat) {
                stack.length = 0;
                if (typeof line.block.header === "number") {
                    root.push(...this._headingNodes(line));
                } else if (line.block.blockquote) {
                    const end = this._runEnd(lines, index, candidate => this._isPlainFormatLine(candidate) && !!candidate.block.blockquote);
                    root.push(this._blockquoteNode(lines.slice(index, end)));
                    index = end - 1;
                } else if (line.block["code-block"]) {
                    const end = this._runEnd(lines, index, candidate => this._isPlainFormatLine(candidate)
                        && !candidate.block.blockquote && !!candidate.block["code-block"]);
                    root.push(...this._codeBlockNodes(lines.slice(index, end)));
                    index = end - 1;
                } else {
                    root.push(...this._paragraphBlocks(line, false));
                }
                continue;
            }

            if (!KNOWN_LIST_FORMATS.has(listFormat)) {
                unknownListFormats.push({line: index, value: listFormat});
            }
            const level = typeof line.block.indent === "number" ? line.block.indent : 0;
            const listType = listFormat === "ordered" ? "orderedList"
                : (listFormat === "checked" || listFormat === "unchecked") ? "taskList"
                    : "bulletList";

            while (stack.length > 0 && (
                stack[stack.length - 1].level > level
                || (stack[stack.length - 1].level === level && stack[stack.length - 1].listType !== listType)
            )) {
                stack.pop();
            }

            let top = stack[stack.length - 1];
            if (!top || top.level < level) {
                // A new, deeper list. Since @o2d/rich-text-editor 0.2.0 a taskItem's content is
                // `paragraph block*` (like listItem's), so a nested list may sit inside a checklist
                // item too — Quill's flat `indent` model is rebuilt one level at a time, keeping each
                // line's OWN list type: a bullet item indented under a checklist becomes a bulletList
                // inside that taskItem, never a checkbox.
                const newList: RichTextDocumentNode = {type: listType, content: []};
                if (!top) {
                    root.push(newList);
                } else {
                    // Nest under the last item of the parent list.
                    const parentContent = top.node.content as RichTextDocumentNode[];
                    const lastItem = parentContent[parentContent.length - 1];
                    (lastItem.content as RichTextDocumentNode[]).push(newList);
                }
                stack.push({level, listType, node: newList});
                top = stack[stack.length - 1];
            }

            // Both item types start with a paragraph and may hold further blocks after it (an image
            // pulled out of the line, or a nested list).
            const listItem: RichTextDocumentNode = top.listType === "taskList"
                ? {type: "taskItem", attrs: {checked: listFormat === "checked"}, content: this._paragraphBlocks(line, true)}
                : {type: "listItem", content: this._paragraphBlocks(line, true)};
            (top.node.content as RichTextDocumentNode[]).push(listItem);
        }

        return root;
    }

    /** Index after the last line of the run starting at `start` whose lines all satisfy `belongs`. */
    private static _runEnd(lines: LineBlock[], start: number, belongs: (line: LineBlock) => boolean): number {
        let end = start + 1;
        while (end < lines.length && belongs(lines[end])) {
            end++;
        }
        return end;
    }

    /** A line that is neither a table cell, a list line nor a heading. */
    private static _isPlainFormatLine(line: LineBlock): boolean {
        return line.block.table === undefined && !line.block.list && typeof line.block.header !== "number";
    }

    private static _alignAttrs(line: LineBlock): Record<string, unknown> {
        return typeof line.block.align === "string" ? {textAlign: line.block.align} : {};
    }

    /**
     * The blocks one line becomes where the schema allows block children: one paragraph, or, when the
     * line holds images, paragraph / image / paragraph with empty halves dropped. `leadingParagraph`
     * keeps an (empty) paragraph first, which a list item requires.
     */
    private static _paragraphBlocks(line: LineBlock, leadingParagraph: boolean): RichTextDocumentNode[] {
        const attrs = this._alignAttrs(line);
        if (!line.inline.some(node => node.kind === "image")) {
            return [this._withContentAndAttrs({type: "paragraph"}, this._inlineNodesToContent(line.inline), attrs)];
        }

        const blocks: RichTextDocumentNode[] = [];
        let run: InlineNode[] = [];
        const flush = () => {
            if (run.length > 0) {
                blocks.push(this._withContentAndAttrs({type: "paragraph"}, this._inlineNodesToContent(run), attrs));
                run = [];
            }
        };
        for (const node of line.inline) {
            if (node.kind === "image") {
                flush();
                blocks.push(this._imageNode(node));
            } else {
                run.push(node);
            }
        }
        flush();

        if (leadingParagraph && blocks[0]?.type !== "paragraph") {
            blocks.unshift({type: "paragraph"});
        }
        return blocks;
    }

    /** A heading holds inline content only; its images follow it as blocks. */
    private static _headingNodes(line: LineBlock): RichTextDocumentNode[] {
        const inline = line.inline.filter(node => node.kind !== "image");
        const heading = this._withContentAndAttrs(
            {type: "heading", attrs: {...this._alignAttrs(line), level: line.block.header}},
            this._inlineNodesToContent(inline),
            {},
        );
        return [heading, ...this._imageNodes(line.inline)];
    }

    private static _blockquoteNode(lines: LineBlock[]): RichTextDocumentNode {
        return {type: "blockquote", content: lines.flatMap(line => this._paragraphBlocks(line, false))};
    }

    /**
     * One code block for a run of code lines, joined with `\n` (a soft break counts as `\n`). The schema
     * allows only unmarked text there, so marks are dropped; images follow the whole block as blocks.
     */
    private static _codeBlockNodes(lines: LineBlock[]): RichTextDocumentNode[] {
        const text = lines
            .map(line => line.inline
                .map(node => node.kind === "text" ? node.text : node.kind === "hardBreak" ? "\n" : "")
                .join(""))
            .join("\n");
        const codeBlock: RichTextDocumentNode = text.length > 0
            ? {type: "codeBlock", content: [{type: "text", text}]}
            : {type: "codeBlock"};
        return [codeBlock, ...lines.flatMap(line => this._imageNodes(line.inline))];
    }

    /**
     * Groups a run of table cell lines into rows by their row id (first appearance decides the row
     * order) and pads short rows with empty cells.
     */
    private static _tableNode(lines: LineBlock[]): RichTextDocumentNode {
        const rows = new Map<string, RichTextDocumentNode[]>();
        for (const line of lines) {
            const rowId = String(line.block.table);
            if (!rows.has(rowId)) {
                rows.set(rowId, []);
            }
            rows.get(rowId)!.push({type: "tableCell", content: this._paragraphBlocks(line, false)});
        }

        const width = Math.max(...[...rows.values()].map(cells => cells.length));
        return {
            type: "table",
            content: [...rows.values()].map(cells => ({
                type: "tableRow",
                content: [
                    ...cells,
                    ...Array.from({length: width - cells.length}, () => ({type: "tableCell", content: [{type: "paragraph"}]})),
                ],
            })),
        };
    }

    private static _imageNode(node: Extract<InlineNode, { kind: "image" }>): RichTextDocumentNode {
        return {type: "image", attrs: {src: node.src, ...node.attrs}};
    }

    private static _imageNodes(inline: InlineNode[]): RichTextDocumentNode[] {
        return inline
            .filter((node): node is Extract<InlineNode, { kind: "image" }> => node.kind === "image")
            .map(node => this._imageNode(node));
    }

    private static _withContentAndAttrs(
        node: RichTextDocumentNode,
        content: RichTextDocumentNode[],
        attrs: Record<string, unknown>,
    ): RichTextDocumentNode {
        const merged: RichTextDocumentNode = {
            ...node,
            ...(Object.keys(attrs).length > 0 ? {attrs: {...node.attrs, ...attrs}} : {}),
        };
        return content.length > 0 ? {...merged, content} : merged;
    }

    private static _inlineNodesToContent(inline: InlineNode[]): RichTextDocumentNode[] {
        const content: RichTextDocumentNode[] = [];
        for (const node of inline) {
            if (node.kind === "hardBreak") {
                content.push({type: "hardBreak"});
            } else if (node.kind === "image") {
                content.push(this._imageNode(node));
            } else {
                const marks = this._buildMarks(node.attrs);
                content.push({
                    type: "text",
                    text: node.text,
                    ...(marks.length > 0 ? {marks} : {}),
                });
            }
        }
        return content;
    }

    private static _buildMarks(attrs: QuillAttributes): RichTextDocumentMark[] {
        // tiptap's `code` mark excludes every other mark on the same text.
        if (attrs.code) {
            return [{type: "code"}];
        }

        const marks: RichTextDocumentMark[] = [];

        if (attrs.bold) {
            marks.push({type: "bold"});
        }
        if (attrs.italic) {
            marks.push({type: "italic"});
        }
        if (attrs.strike) {
            marks.push({type: "strike"});
        }
        if (attrs.underline) {
            marks.push({type: "underline"});
        }
        if (attrs.script === "super") {
            marks.push({type: "superscript"});
        } else if (attrs.script === "sub") {
            marks.push({type: "subscript"});
        }
        if (typeof attrs.link === "string") {
            marks.push({type: "link", attrs: {href: attrs.link}});
        }
        if (typeof attrs.background === "string") {
            marks.push({type: "highlight", attrs: {color: attrs.background}});
        }

        // `color` and `size` both ride the SAME mark type (`textStyle`, the
        // canonical inline-style carrier — see `rich-text-extensions.ts`) —
        // a ProseMirror text node can only carry ONE mark instance per mark
        // type, so these merge into a single mark rather than two.
        const textStyleAttrs: Record<string, unknown> = {};
        if (typeof attrs.color === "string") {
            textStyleAttrs.color = attrs.color;
        }
        if (typeof attrs.size === "string" && SIZE_TO_FONT_SIZE[attrs.size]) {
            textStyleAttrs.fontSize = SIZE_TO_FONT_SIZE[attrs.size];
        }
        if (Object.keys(textStyleAttrs).length > 0) {
            marks.push({type: "textStyle", attrs: textStyleAttrs});
        }

        return marks;
    }

}
